import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatMoney } from "@/lib/currency";
import {
  BarChart3,
  Kanban,
  Target,
  Table as TableIcon,
  Plus,
  Trash2,
  AlertTriangle,
  Flag,
  MessageCircle,
  Mail,
  MousePointerClick,
  Eye,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const CHART_COLORS = ["#F97316", "#A855F7", "#EAB308", "#14B8A6", "#F43F5E", "#3B82F6"];
const TABS = [
  { key: "kanban", label: "Kanban", icon: Kanban },
  { key: "log", label: "Lead Log", icon: TableIcon },
  { key: "dashboard", label: "Dashboard", icon: BarChart3 },
  { key: "projection", label: "Projection", icon: Target },
  { key: "outreach", label: "Outreach Analytics", icon: MessageCircle },
];

const EMPTY_LEAD = {
  contact: { name: "", company: "", email: "", phone: "", source: "", setter_name: "", closer_name: "" },
  status: "New",
  dates: { first_contact_at: "", meeting_booked_at: "", meeting_at: "", paid_in_full_at: "" },
  meeting_status: "",
  offer_made: false,
  sale_type: "",
  loss_reason: "",
  money: { deposit_amount: 0, total_deal_value: 0, cash_collected: 0, refund_amount: 0, commission_pct: 0 },
  notes: "",
};

const money = (n) => formatMoney(n);

export default function SalesCRM() {
  const [tab, setTab] = useState("kanban");
  const [leads, setLeads] = useState([]);
  const [constants, setConstants] = useState({ statuses: [], meeting_statuses: [], loss_reasons: [], sale_types: [] });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelId, setConfirmDelId] = useState(null);

  const load = async () => {
    try {
      const [{ data: c }, { data: ls }] = await Promise.all([
        api.get("/admin/crm/constants"),
        api.get("/admin/crm/leads"),
      ]);
      setConstants(c);
      setLeads(ls);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load CRM data.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    try {
      if (payload.id) {
        const { id, __new, ...rest } = payload;
        const { data } = await api.patch(`/admin/crm/leads/${id}`, rest);
        setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
        toast.success("Lead updated");
      } else {
        const { __new, ...rest } = payload;
        const { data } = await api.post("/admin/crm/leads", rest);
        setLeads((prev) => [data, ...prev]);
        toast.success("Lead created");
      }
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed. Check the required fields.");
    }
  };

  const del = async (id) => {
    setConfirmDelId(id);
  };

  const confirmDelete = async () => {
    const id = confirmDelId;
    if (!id) return;
    try {
      await api.delete(`/admin/crm/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      setEditing(null);
      toast.success("Lead deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
    setConfirmDelId(null);
  };

  const moveStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/crm/leads/${id}`, { status });
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed.");
      // Resync from server so the UI doesn't drift if the server state
      // diverges from what we optimistically expected.
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="sales-crm">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-primary mb-2">Sales CRM</div>
          <h1 className="font-display text-4xl leading-tight">Setters, closers, cash.</h1>
          <p className="text-muted-foreground mt-1">One board. Everything downstream auto-calculates.</p>
        </div>
        <Button data-testid="crm-new-lead-btn" onClick={() => setEditing({ ...EMPTY_LEAD, __new: true })} className="rounded-full gap-1.5">
          <Plus className="h-4 w-4" /> New lead
        </Button>
      </div>

      {/* Tabs */}
      <div className="inline-flex rounded-full border border-border bg-secondary/50 p-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`crm-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] transition-colors ${tab === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground">Loading…</div>
      ) : (
        <>
          {tab === "kanban" && <KanbanView leads={leads} constants={constants} onCardClick={setEditing} onMoveStatus={moveStatus} />}
          {tab === "log" && <LogView leads={leads} onEdit={setEditing} onDelete={del} />}
          {tab === "dashboard" && <DashboardView />}
          {tab === "projection" && <ProjectionView />}
          {tab === "outreach" && <OutreachAnalyticsView />}
        </>
      )}

      {editing && (
        <LeadForm lead={editing} constants={constants} onSave={save} onClose={() => setEditing(null)} onDelete={del} />
      )}

      <AlertDialog open={!!confirmDelId} onOpenChange={(o) => !o && setConfirmDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. Historical metrics will recompute immediately.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="crm-lead-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ================================================================
// Kanban
// ================================================================
function KanbanView({ leads, constants, onCardClick, onMoveStatus }) {
  const grouped = useMemo(() => {
    const g = {};
    (constants.statuses || []).forEach((s) => (g[s] = []));
    leads.forEach((l) => {
      (g[l.status] || g["New"]).push(l);
    });
    return g;
  }, [leads, constants.statuses]);

  const onDragStart = (e, id) => e.dataTransfer.setData("text/plain", id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (id) onMoveStatus(id, status);
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-3" data-testid="crm-kanban">
      {(constants.statuses || []).map((status) => (
        <div
          key={status}
          onDragOver={onDragOver}
          onDrop={(e) => onDrop(e, status)}
          data-testid={`crm-kanban-col-${status}`}
          className="min-w-[280px] w-72 flex-shrink-0 rounded-xl border border-border bg-card/60 p-3"
        >
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{status}</div>
            <div className="text-xs text-muted-foreground">{grouped[status].length}</div>
          </div>
          <div className="space-y-2">
            {grouped[status].map((l) => (
              <div
                key={l.id}
                draggable
                onDragStart={(e) => onDragStart(e, l.id)}
                onClick={() => onCardClick(l)}
                data-testid={`crm-lead-card-${l.id}`}
                className="rounded-lg border border-border bg-card p-3 cursor-grab hover:border-primary/50 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold truncate">{l.contact?.name || "—"}</div>
                  {l.aging_days !== null && l.aging_days >= 7 && (
                    <span title="Aged 7+ days" className="text-destructive"><AlertTriangle className="h-3.5 w-3.5" /></span>
                  )}
                </div>
                {l.contact?.company && <div className="text-xs text-muted-foreground truncate">{l.contact.company}</div>}
                <div className="flex items-center justify-between mt-2 text-xs">
                  <span className="text-muted-foreground">{l.contact?.setter_name || ""} → {l.contact?.closer_name || ""}</span>
                  <span className="text-primary font-semibold">{money(l.money?.total_deal_value)}</span>
                </div>
                {l.status === "Lost" && l.loss_reason && (
                  <div className="text-[10px] uppercase tracking-wide text-destructive mt-1">Lost · {l.loss_reason}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ================================================================
// Lead Log
// ================================================================
function LogView({ leads, onEdit, onDelete }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const filtered = leads.filter((l) => {
    if (status !== "all" && l.status !== status) return false;
    if (q) {
      const s = q.toLowerCase();
      const hay = `${l.contact?.name || ""} ${l.contact?.company || ""} ${l.contact?.email || ""} ${l.contact?.setter_name || ""} ${l.contact?.closer_name || ""}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });
  const total = filtered.reduce((s, l) => s + Number(l.money?.total_deal_value || 0), 0);

  return (
    <div className="space-y-3" data-testid="crm-log">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search name, company, email, rep…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="crm-log-search" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="crm-log-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["New", "Proposal", "Deposit", "Follow-Up Ongoing", "Meeting Follow-Up", "Won", "Lost"].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-muted-foreground">{filtered.length} leads · {money(total)}</div>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <tr>
              {["Name", "Company", "Setter", "Closer", "Status", "Meeting", "Deal", "Cash", "Earnings", ""].map((h) => (
                <th key={h} className="text-left px-3 py-2">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((l) => (
              <tr key={l.id} className="hover:bg-secondary/30 cursor-pointer" onClick={() => onEdit(l)} data-testid={`crm-log-row-${l.id}`}>
                <td className="px-3 py-2 font-semibold">{l.contact?.name || "—"}</td>
                <td className="px-3 py-2 text-muted-foreground">{l.contact?.company || ""}</td>
                <td className="px-3 py-2">{l.contact?.setter_name || ""}</td>
                <td className="px-3 py-2">{l.contact?.closer_name || ""}</td>
                <td className="px-3 py-2"><span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{l.status}</span></td>
                <td className="px-3 py-2 text-xs">{l.meeting_status || "—"}</td>
                <td className="px-3 py-2">{money(l.money?.total_deal_value)}</td>
                <td className="px-3 py-2">{money(l.money?.cash_collected)}</td>
                <td className="px-3 py-2 text-primary font-semibold">{money(l.earnings)}</td>
                <td className="px-3 py-2 text-right">
                  <button className="text-muted-foreground hover:text-destructive p-1" onClick={(e) => { e.stopPropagation(); onDelete(l.id); }} data-testid={`crm-log-delete-${l.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">No leads match the filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ================================================================
// Dashboard
// ================================================================
function DashboardView() {
  const [m, setM] = useState(null);
  const [goal, setGoal] = useState("");
  const [activity, setActivity] = useState([]);
  const [confirmActivity, setConfirmActivity] = useState(null);

  const reload = async () => {
    try {
      const [{ data: metrics }, { data: acts }] = await Promise.all([
        api.get("/admin/crm/metrics"),
        api.get("/admin/crm/setter-activity"),
      ]);
      setM(metrics);
      setActivity(acts);
    } catch { toast.error("Could not load dashboard"); }
  };
  useEffect(() => { reload(); }, []);

  const saveGoal = async () => {
    if (!goal) return;
    try {
      await api.patch("/admin/crm/settings/revenue-goal", { revenue_goal: Number(goal) });
      toast.success("Goal updated");
      reload();
      setGoal("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save goal.");
    }
  };

  const deleteActivity = async () => {
    if (!confirmActivity) return;
    try {
      await api.delete(`/admin/crm/setter-activity/${confirmActivity.id}`);
      toast.success("Activity row removed");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
    setConfirmActivity(null);
  };

  if (!m) return <div className="text-center py-16 text-muted-foreground">Loading metrics…</div>;

  const lossData = Object.entries(m.loss_reasons || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      {/* Money row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Revenue" value={money(m.money.revenue)} testid="crm-metric-revenue" />
        <MetricCard label="Cash collected" value={money(m.money.cash_collected)} testid="crm-metric-cash" />
        <MetricCard label="Refunds" value={money(m.money.refunds)} testid="crm-metric-refunds" />
        <MetricCard label="Net revenue" value={money(m.money.net_revenue)} testid="crm-metric-net" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Total sales" value={m.money.total_sales} />
        <MetricCard label="Deposits paid" value={money(m.money.deposits_paid)} />
        <MetricCard label="Deposit→Paid %" value={`${m.money.deposit_to_paid_pct || 0}%`} />
        <MetricCard label="Days to collect" value={m.money.days_to_collect_avg ?? "—"} />
      </div>

      {/* Goal */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Revenue goal</div>
            <div className="font-display text-2xl">{money(m.money.revenue_goal)}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Completion</div>
            <div className="font-display text-2xl text-primary">{m.money.goal_completion_pct}%</div>
          </div>
        </div>
        <div className="h-2 bg-secondary rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, m.money.goal_completion_pct || 0)}%` }} />
        </div>
        <div className="flex gap-2">
          <Input placeholder={`Set new goal (${(typeof document !== "undefined" && document.documentElement.dataset.currency) || "USD"})`} value={goal} onChange={(e) => setGoal(e.target.value)} className="max-w-xs" data-testid="crm-goal-input" />
          <Button onClick={saveGoal} className="rounded-full" data-testid="crm-goal-save">Save</Button>
        </div>
      </div>

      {/* Setters */}
      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="font-display text-lg mb-3">Setters</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="text-muted-foreground uppercase tracking-wider">
              <tr>
                {["Setter", "Leads", "Dials", "DMs", "Convs", "Booked", "Taken", "Show %", "Conv→Book %", "Speed (min)", "Lag (d)", "DQ %"].map((h) => <th key={h} className="text-left px-2 py-1">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {Object.entries(m.setters).map(([name, s]) => (
                <tr key={name}>
                  <td className="px-2 py-1.5 font-semibold">{name}</td>
                  <td className="px-2 py-1.5">{s.leads || 0}</td>
                  <td className="px-2 py-1.5">{s.dials || 0}</td>
                  <td className="px-2 py-1.5">{s.dms || 0}</td>
                  <td className="px-2 py-1.5">{s.conversations || 0}</td>
                  <td className="px-2 py-1.5">{s.calls_scheduled || 0}</td>
                  <td className="px-2 py-1.5">{s.calls_taken || 0}</td>
                  <td className="px-2 py-1.5">{s.show_up_rate || 0}%</td>
                  <td className="px-2 py-1.5">{s.conv_to_booked_pct || 0}%</td>
                  <td className="px-2 py-1.5">{s.speed_to_lead_min ?? "—"}</td>
                  <td className={`px-2 py-1.5 ${(s.booking_lag_days || 0) > 4 ? "text-destructive font-semibold" : ""}`}>{s.booking_lag_days ?? "—"}</td>
                  <td className="px-2 py-1.5">{s.dq_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Closers */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="font-display text-lg mb-3">Closers</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="text-muted-foreground uppercase tracking-wider">
                <tr>{["Closer", "Taken", "Offers", "Offer %", "Close %", "on Off %", "1-C", "F-U", "Avg deal", "RPC"].map((h) => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-border">
                {Object.entries(m.closers).map(([name, s]) => (
                  <tr key={name}>
                    <td className="px-2 py-1.5 font-semibold">{name}</td>
                    <td className="px-2 py-1.5">{s.calls_taken}</td>
                    <td className="px-2 py-1.5">{s.offers_made}</td>
                    <td className="px-2 py-1.5">{s.offer_rate}%</td>
                    <td className="px-2 py-1.5">{s.close_rate}%</td>
                    <td className="px-2 py-1.5">{s.close_rate_on_offers}%</td>
                    <td className="px-2 py-1.5">{s.sales_1_call}</td>
                    <td className="px-2 py-1.5">{s.sales_follow_up}</td>
                    <td className="px-2 py-1.5">{money(s.avg_deal_size)}</td>
                    <td className="px-2 py-1.5">{money(s.rpc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="font-display text-lg mb-3">Loss reasons</h2>
          {lossData.length ? (
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={lossData} dataKey="value" nameKey="name" outerRadius={80}>
                    {lossData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#111", border: "1px solid #333" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-muted-foreground text-sm">No losses yet.</div>
          )}
        </div>
      </div>

      {/* Aging alert */}
      {m.aging_count > 0 && (
        <div data-testid="crm-aging-alert" className="rounded-xl border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <div>
            <div className="font-semibold">{m.aging_count} follow-ups have been untouched 7+ days.</div>
            <div className="text-xs text-muted-foreground">Rescue them before they go cold.</div>
          </div>
        </div>
      )}

      {/* Setter activity log with per-row delete */}
      <div className="rounded-xl border border-border bg-card p-4" data-testid="crm-activity-log">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg">Setter activity log</h2>
          <div className="text-xs text-muted-foreground">{activity.length} rows</div>
        </div>
        {activity.length === 0 ? (
          <div className="text-muted-foreground text-sm py-4 text-center">No setter activity logged yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="text-muted-foreground uppercase tracking-wider">
                <tr>
                  {["Setter", "Date", "Dials", "DMs", "Convs", ""].map((h) => <th key={h} className="text-left px-2 py-1">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {activity.map((a) => (
                  <tr key={a.id} data-testid={`crm-activity-row-${a.id}`}>
                    <td className="px-2 py-1.5 font-semibold">{a.setter_name}</td>
                    <td className="px-2 py-1.5">{a.date}</td>
                    <td className="px-2 py-1.5">{a.dials || 0}</td>
                    <td className="px-2 py-1.5">{a.dms || 0}</td>
                    <td className="px-2 py-1.5">{a.conversations || 0}</td>
                    <td className="px-2 py-1.5 text-right">
                      <button
                        onClick={() => setConfirmActivity(a)}
                        className="text-muted-foreground hover:text-destructive p-1"
                        title="Remove this activity row"
                        data-testid={`crm-activity-delete-${a.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={!!confirmActivity} onOpenChange={(o) => !o && setConfirmActivity(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this activity row?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmActivity && `${confirmActivity.setter_name} · ${confirmActivity.date} · ${confirmActivity.dials || 0} dials · ${confirmActivity.dms || 0} DMs`}
              . Setter dashboard metrics will refresh immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteActivity} data-testid="crm-activity-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ================================================================
// Projection
// ================================================================
function ProjectionView() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/admin/crm/projection").then(({ data }) => setP(data)); }, []);
  if (!p) return <div className="text-center py-16 text-muted-foreground">Loading projection…</div>;

  const cases = [
    { name: "Worst", value: p.forecast.worst_case_revenue, fill: "#F43F5E" },
    { name: "Expected", value: p.forecast.expected_revenue, fill: "#F97316" },
    { name: "Best", value: p.forecast.best_case_revenue, fill: "#A855F7" },
  ];

  return (
    <div className="space-y-6" data-testid="crm-projection">
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap gap-6">
          <ProjChip label="Upcoming meetings" value={p.upcoming_meetings} />
          <ProjChip label="Show rate" value={`${Math.round(p.rates.show_rate * 100)}%`} />
          <ProjChip label="Offer rate" value={`${Math.round(p.rates.offer_rate * 100)}%`} />
          <ProjChip label="Close rate" value={`${Math.round(p.rates.close_rate * 100)}%`} />
          <ProjChip label="Avg deal size" value={money(p.rates.avg_deal_size)} />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <ForecastCard title="Worst case" value={p.forecast.worst_case_revenue} tone="destructive" />
        <ForecastCard title="Expected" value={p.forecast.expected_revenue} tone="primary" />
        <ForecastCard title="Best case" value={p.forecast.best_case_revenue} tone="accent" />
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="font-display text-lg mb-3">End-of-month forecast</h2>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={cases}>
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#111", border: "1px solid #333" }} formatter={(v) => money(v)} />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {cases.map((c, i) => <Cell key={i} fill={c.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">
        Forecast = <b>upcoming_meetings × show_rate × offer_rate × close_rate × avg_deal_size</b>.
        Rates auto-derive from historical data.
      </div>
    </div>
  );
}

function ProjChip({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</span>
      <span className="font-display text-2xl">{value}</span>
    </div>
  );
}

function ForecastCard({ title, value, tone }) {
  const bg = tone === "destructive" ? "border-destructive/40 bg-destructive/10" : tone === "accent" ? "border-accent/40 bg-accent/10" : "border-primary/40 bg-primary/10";
  return (
    <div className={`rounded-xl border p-4 ${bg}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{title}</div>
      <div className="font-display text-3xl mt-2">{money(value)}</div>
    </div>
  );
}

function MetricCard({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="font-display text-xl mt-1">{value}</div>
    </div>
  );
}

// ================================================================
// Lead form modal
// ================================================================
function LeadForm({ lead, constants, onSave, onClose, onDelete }) {
  const [form, setForm] = useState({
    ...EMPTY_LEAD,
    ...lead,
    contact: { ...EMPTY_LEAD.contact, ...(lead.contact || {}) },
    dates: { ...EMPTY_LEAD.dates, ...(lead.dates || {}) },
    money: { ...EMPTY_LEAD.money, ...(lead.money || {}) },
  });

  const setC = (k, v) => setForm((f) => ({ ...f, contact: { ...f.contact, [k]: v } }));
  const setD = (k, v) => setForm((f) => ({ ...f, dates: { ...f.dates, [k]: v } }));
  const setM = (k, v) => setForm((f) => ({ ...f, money: { ...f.money, [k]: Number(v) || 0 } }));

  const submit = (e) => {
    e.preventDefault();
    if (form.status === "Lost" && !form.loss_reason) {
      toast.error("Loss reason is required when status is Lost.");
      return;
    }
    // strip empty date strings so backend receives null → keeps existing value
    const cleanDates = Object.fromEntries(Object.entries(form.dates).filter(([, v]) => v !== ""));
    onSave({ ...form, dates: cleanDates });
  };

  const isNew = form.__new;
  const earnings = ((form.money.cash_collected || 0) - (form.money.refund_amount || 0)) * (form.money.commission_pct || 0) / 100;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="crm-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Flag className="h-5 w-5" /> {isNew ? "New lead" : form.contact.name || "Edit lead"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {/* Contact */}
          <fieldset className="grid grid-cols-2 gap-3">
            <Field label="Name" required><Input data-testid="crm-form-name" required value={form.contact.name} onChange={(e) => setC("name", e.target.value)} /></Field>
            <Field label="Company"><Input value={form.contact.company} onChange={(e) => setC("company", e.target.value)} /></Field>
            <Field label="Email"><Input type="email" value={form.contact.email} onChange={(e) => setC("email", e.target.value)} /></Field>
            <Field label="Phone"><Input value={form.contact.phone} onChange={(e) => setC("phone", e.target.value)} /></Field>
            <Field label="Source"><Input value={form.contact.source} onChange={(e) => setC("source", e.target.value)} /></Field>
            <Field label="Setter"><Input value={form.contact.setter_name} onChange={(e) => setC("setter_name", e.target.value)} /></Field>
            <Field label="Closer"><Input value={form.contact.closer_name} onChange={(e) => setC("closer_name", e.target.value)} /></Field>
          </fieldset>

          {/* Status + meeting */}
          <fieldset className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="crm-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>{constants.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Meeting status">
              <Select value={form.meeting_status || "none"} onValueChange={(v) => setForm((f) => ({ ...f, meeting_status: v === "none" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {constants.meeting_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Offer made?">
              <Select value={form.offer_made ? "yes" : "no"} onValueChange={(v) => setForm((f) => ({ ...f, offer_made: v === "yes" }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Sale type">
              <Select value={form.sale_type || "none"} onValueChange={(v) => setForm((f) => ({ ...f, sale_type: v === "none" ? "" : v }))}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {constants.sale_types.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            {form.status === "Lost" && (
              <Field label="Loss reason" required>
                <Select value={form.loss_reason || ""} onValueChange={(v) => setForm((f) => ({ ...f, loss_reason: v }))}>
                  <SelectTrigger data-testid="crm-form-loss-reason"><SelectValue placeholder="Choose reason" /></SelectTrigger>
                  <SelectContent>{constants.loss_reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
          </fieldset>

          {/* Dates */}
          <fieldset className="grid grid-cols-2 gap-3">
            <Field label="First contact"><Input type="datetime-local" value={form.dates.first_contact_at?.slice(0, 16) || ""} onChange={(e) => setD("first_contact_at", e.target.value)} /></Field>
            <Field label="Meeting booked"><Input type="datetime-local" value={form.dates.meeting_booked_at?.slice(0, 16) || ""} onChange={(e) => setD("meeting_booked_at", e.target.value)} /></Field>
            <Field label="Meeting date"><Input type="datetime-local" value={form.dates.meeting_at?.slice(0, 16) || ""} onChange={(e) => setD("meeting_at", e.target.value)} /></Field>
            <Field label="Paid in full"><Input type="datetime-local" value={form.dates.paid_in_full_at?.slice(0, 16) || ""} onChange={(e) => setD("paid_in_full_at", e.target.value)} /></Field>
          </fieldset>

          {/* Money */}
          <fieldset className="grid grid-cols-2 gap-3">
            <Field label="Total deal value"><Input type="number" step="0.01" value={form.money.total_deal_value} onChange={(e) => setM("total_deal_value", e.target.value)} data-testid="crm-form-deal" /></Field>
            <Field label="Deposit amount"><Input type="number" step="0.01" value={form.money.deposit_amount} onChange={(e) => setM("deposit_amount", e.target.value)} /></Field>
            <Field label="Cash collected"><Input type="number" step="0.01" value={form.money.cash_collected} onChange={(e) => setM("cash_collected", e.target.value)} /></Field>
            <Field label="Refund/clawback"><Input type="number" step="0.01" value={form.money.refund_amount} onChange={(e) => setM("refund_amount", e.target.value)} /></Field>
            <Field label="Commission %"><Input type="number" step="0.1" value={form.money.commission_pct} onChange={(e) => setM("commission_pct", e.target.value)} data-testid="crm-form-commission" /></Field>
            <Field label="Earnings (auto)"><Input readOnly value={money(earnings)} data-testid="crm-form-earnings" /></Field>
          </fieldset>

          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></Field>

          <DialogFooter className="gap-2">
            {!isNew && (
              <Button type="button" variant="outline" className="rounded-full" onClick={() => onDelete(lead.id)} data-testid="crm-form-delete">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
            <Button type="submit" className="rounded-full" data-testid="crm-form-save">{isNew ? "Create" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <Label className="text-xs">{label}{required && " *"}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}



// ================================================================
// Outreach Analytics — WhatsApp + Email side-by-side, with Top
// Performing Region per pipeline (5 mini charts).
// ================================================================

const PIPELINE_META = [
  { key: "b2c_jaydiets", label: "JAYDIETs", accent: "#22C55E" },
  { key: "b2c_coffee",   label: "Coffee",   accent: "#F59E0B" },
  { key: "b2c_wellness", label: "Wellness", accent: "#8B5CF6" },
  { key: "b2c_retreat",  label: "Retreat",  accent: "#06B6D4" },
  { key: "b2b_growth",   label: "B2B Growth", accent: "#F97316" },
];

const BUTTON_META = {
  jay7_trial: { label: "JAY 7 Trial", color: "#22C55E" },
  coffee:     { label: "Coffee",      color: "#F59E0B" },
  training:   { label: "Training",    color: "#8B5CF6" },
};

function OutreachAnalyticsView() {
  const [channel, setChannel] = useState("whatsapp");
  const [analytics, setAnalytics] = useState(null);
  const [topRegions, setTopRegions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: a }, { data: r }] = await Promise.all([
          api.get("/whatsapp/analytics"),
          api.get("/whatsapp/top-regions"),
        ]);
        setAnalytics(a);
        setTopRegions(r?.by_pipeline || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load outreach analytics");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-center py-16 text-muted-foreground">Loading…</div>;
  const wa = analytics?.whatsapp || {};
  const em = analytics?.email || {};

  const chan = channel === "whatsapp" ? wa : em;
  const funnel = [
    { key: "sent", label: "Sent",       value: (chan.by_status?.sent || 0) + (chan.by_status?.delivered || 0) + (chan.by_status?.read || 0), icon: Mail, color: "#3B82F6" },
    { key: "delivered", label: "Delivered", value: (chan.by_status?.delivered || 0) + (chan.by_status?.read || 0), icon: CheckCircle2, color: "#10B981" },
    { key: "opened", label: "Opened",   value: chan.by_status?.read || 0, icon: Eye, color: "#8B5CF6" },
    { key: "clicked", label: "Clicked", value: chan.clicked || 0, icon: MousePointerClick, color: "#F59E0B" },
    { key: "failed", label: "Failed",   value: (chan.by_status?.failed || 0) + (chan.by_status?.undelivered || 0), icon: AlertTriangle, color: "#EF4444" },
  ];

  const buttonBreakdown = Object.entries(wa.by_button || {}).map(([id, count]) => ({
    id, label: BUTTON_META[id]?.label || id, count, color: BUTTON_META[id]?.color || "#94A3B8",
  }));

  return (
    <div className="space-y-6" data-testid="crm-outreach-analytics">
      {/* Channel toggle */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex rounded-full border border-border bg-secondary/50 p-1">
          <button
            onClick={() => setChannel("whatsapp")}
            data-testid="crm-outreach-tab-whatsapp"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] transition-colors ${channel === "whatsapp" ? "bg-emerald-500 text-white" : "text-muted-foreground hover:text-foreground"}`}
          >
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </button>
          <button
            onClick={() => setChannel("email")}
            data-testid="crm-outreach-tab-email"
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] transition-colors ${channel === "email" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Mail className="h-3.5 w-3.5" /> Email
          </button>
        </div>
        <div className="text-xs text-muted-foreground">
          Comparing channels for JAY 7 Trial + Coffee + Training CTAs. WhatsApp click-through beats email for
          local markets; use this to decide where to invest per country.
        </div>
      </div>

      {/* Funnel KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {funnel.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.key} className="rounded-xl border border-border bg-card p-4" data-testid={`crm-outreach-kpi-${s.key}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                <Icon className="h-3.5 w-3.5" style={{ color: s.color }} />
                {s.label}
              </div>
              <div className="font-display text-3xl mt-2 tabular-nums" style={{ color: s.color }}>{s.value.toLocaleString()}</div>
            </div>
          );
        })}
      </div>

      {/* WhatsApp-only button breakdown */}
      {channel === "whatsapp" && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <MousePointerClick className="h-4 w-4 text-amber-500" />
            <h3 className="font-display text-lg">CTA button taps</h3>
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground ml-1">
              which button is winning · click-through by lead intent
            </span>
          </div>
          {buttonBreakdown.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8" data-testid="crm-outreach-no-clicks">
              No CTA taps yet. Send a WhatsApp batch and watch this fill up in real time.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {buttonBreakdown.map((b) => (
                <div key={b.id} className="rounded-xl border border-border/60 bg-secondary/30 p-4" data-testid={`crm-outreach-btn-${b.id}`}>
                  <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{b.label}</div>
                  <div className="font-display text-3xl mt-1.5 tabular-nums" style={{ color: b.color }}>
                    {b.count.toLocaleString()}
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${(b.count / (wa.clicked || 1)) * 100}%`,
                        backgroundColor: b.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Top Performing Region per pipeline (5 mini charts) */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Target className="h-4 w-4 text-primary" />
          <h3 className="font-display text-lg">Top performing regions</h3>
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground ml-1">
            country clicks per pipeline · signals where to double down
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PIPELINE_META.map((p) => {
            const rows = topRegions[p.key] || [];
            const max = Math.max(1, ...rows.map((r) => r.clicks));
            return (
              <div key={p.key} className="rounded-xl border border-border/60 bg-secondary/20 p-4" data-testid={`crm-outreach-region-${p.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.accent }} />
                  <div className="font-medium text-sm">{p.label}</div>
                  <div className="ml-auto text-[11px] text-muted-foreground tabular-nums">
                    {rows.reduce((a, r) => a + r.clicks, 0)} click{rows.length === 1 ? "" : "s"}
                  </div>
                </div>
                {rows.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic">No signups yet.</div>
                ) : (
                  <div className="space-y-2">
                    {rows.map((r) => (
                      <div key={r.country} className="flex items-center gap-2 text-xs">
                        <div className="w-28 truncate">{r.country}</div>
                        <div className="flex-1 h-2 rounded-full bg-background overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${(r.clicks / max) * 100}%`, backgroundColor: p.accent }}
                          />
                        </div>
                        <div className="w-8 text-right tabular-nums font-mono">{r.clicks}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="text-[11px] text-muted-foreground text-center">
        Data refreshes on tab switch. WhatsApp status updates arrive in real-time via Twilio webhooks (queued → sent → delivered → read → clicked).
      </div>
    </div>
  );
}
